/**
 * PERSONA 5 ROYAL MASTER TIMELINE SEQUENCE (GSAP 3 - AAA MOTION POLISHED)
 * Mechanics: Anticipation, Kinetic Weight, Elastic Overshoot, and Ambient Sub-pixel Idle Physics.
 */

class Persona5NavigationMotion {
  constructor(overlay) {
    this.overlay = overlay;
    if (!this.overlay) return;

    this.backdrop = this.overlay.querySelector('.p5r-layer-2-backdrop');
    this.shadowDrift = this.overlay.querySelector('.p5r-bg-shadow-drift');
    this.blackPanel = this.overlay.querySelector('.p5r-layer-4-polygon');
    this.redPoly = this.overlay.querySelector('.p5r-layer-4-red-poly');
    this.topSlash = this.overlay.querySelector('.p5r-layer-5-slash');
    this.cardStack = this.overlay.querySelector('.p5r-card-stack');
    this.cardWrappers = this.overlay.querySelectorAll('.p5r-menu-card-wrapper');
    this.descPanel = this.overlay.querySelector('.p5r-description-panel');
    this.hudStamp = this.overlay.querySelector('.p5r-hud-stamp');
    this.graphicStamps = this.overlay.querySelectorAll('.p5r-graphic-stamp');
    this.titleHeader = this.overlay.querySelector('.p5r-menu-title-header');
    this.confirmPrompt = this.overlay.querySelector('.p5r-confirm-prompt');
    this.closeBtn = this.overlay.querySelector('.p5r-close-trigger');
    this.homeBackBtn = this.overlay.querySelector('.p5r-nav-home-back-btn');

    this.timeline = null;
    this.idleTimeline = null;
    this.splitTypes = [];

    this.init();
  }

  init() {
    this.initSplitType();
    this.buildMasterTimeline();
  }

  initSplitType() {
    if (typeof SplitType === 'undefined') return;

    const targets = this.overlay.querySelectorAll('.p5r-splittype-target');
    targets.forEach(target => {
      try {
        const split = new SplitType(target, { types: 'words' });
        this.splitTypes.push(split);
      } catch (err) {
        console.warn('SplitType initialization error:', err);
      }
    });
  }

  buildMasterTimeline() {
    if (typeof gsap === 'undefined') return;

    // Reset initial hardware accelerated states
    gsap.set(this.backdrop, { opacity: 0 });
    if (this.shadowDrift) {
      gsap.set(this.shadowDrift, { opacity: 0, scale: 0.75, rotate: -5 });
    }
    gsap.set(this.blackPanel, { xPercent: -100, scaleX: 1, scale: 1 });
    gsap.set(this.redPoly, { xPercent: -100, translateY: '0%', scale: 1 });
    gsap.set(this.topSlash, { scaleX: 0 });
    
    if (this.closeBtn) {
      gsap.set(this.closeBtn, { opacity: 0, y: -30, rotate: 6 });
    }

    if (this.homeBackBtn) {
      gsap.set(this.homeBackBtn, { opacity: 0, y: 30, x: 30 });
    }
    
    if (this.hudStamp) {
      gsap.set(this.hudStamp, { opacity: 0, x: -20 });
    }

    if (this.titleHeader) {
      gsap.set(this.titleHeader, { opacity: 0, x: -35, rotate: -8 });
    }

    if (this.confirmPrompt) {
      gsap.set(this.confirmPrompt, { opacity: 0, y: 20 });
    }

    if (this.graphicStamps.length > 0) {
      gsap.set(this.graphicStamps, { opacity: 0, scale: 1.3 });
    }

    gsap.set(this.cardWrappers, {
      x: -180,
      rotate: -18,
      opacity: 0,
      scale: 0.9
    });

    if (this.descPanel) {
      gsap.set(this.descPanel, {
        y: 45,
        rotate: 5,
        opacity: 0
      });
    }

    // Build GSAP Master Timeline with exact offset markers
    this.timeline = gsap.timeline({
      paused: true,
      onStart: () => this.triggerImpactJitter(),
      onComplete: () => this.startIdlePhysics()
    });

    // 50ms: Dark overlay & Ambient Shadow Drift fade/expand in
    this.timeline.to(this.backdrop, {
      opacity: 1,
      duration: 0.2,
      ease: 'power2.out'
    }, 0.05);

    if (this.shadowDrift) {
      this.timeline.to(this.shadowDrift, {
        opacity: 1,
        scale: 1,
        rotate: 0,
        duration: 0.5,
        ease: 'power3.out'
      }, 0.05);
    }

    // 90ms: Top HUD Stamp & P5R Title Header slide in
    if (this.hudStamp) {
      this.timeline.to(this.hudStamp, {
        opacity: 1,
        x: 0,
        duration: 0.25,
        ease: 'power3.out'
      }, 0.09);
    }

    if (this.titleHeader) {
      this.timeline.to(this.titleHeader, {
        opacity: 1,
        x: 0,
        rotate: -4,
        duration: 0.35,
        ease: 'back.out(2)'
      }, 0.08);
    }

    if (this.confirmPrompt) {
      this.timeline.to(this.confirmPrompt, {
        opacity: 1,
        y: 0,
        duration: 0.3,
        ease: 'power3.out'
      }, 0.35);
    }

    if (this.graphicStamps.length > 0) {
      this.timeline.to(this.graphicStamps, {
        opacity: 0.035,
        scale: 1,
        duration: 0.45,
        stagger: 0.08,
        ease: 'power4.out'
      }, 0.12);
    }

    // 100ms: Large black angular panel sweeps in from left
    this.timeline.to(this.blackPanel, {
      xPercent: 0,
      duration: 0.35,
      ease: 'expo.out'
    }, 0.10);

    // 180ms: Large red polygon sweeps in from left
    this.timeline.to(this.redPoly, {
      xPercent: 0,
      duration: 0.4,
      ease: 'power4.out'
    }, 0.18);

    // 250ms: Top red slash sweeps across screen from left (high kinetic velocity)
    this.timeline.to(this.topSlash, {
      scaleX: 1,
      duration: 0.25,
      ease: 'expo.inOut'
    }, 0.25);

    // Dynamic Theme Editor Motion Settings
    const staggerSec = (parseFloat(this.overlay.dataset.staggerSpeed) || 55) / 1000;
    const overshootVal = (parseFloat(this.overlay.dataset.overshoot) || 22) / 10;
    const easeString = `back.out(${overshootVal})`;

    // 320ms: Asymmetrical menu cards fly in with anticipation & overshoot curve
    this.timeline.to(this.cardWrappers, {
      x: 0,
      rotate: (i, target) => parseFloat(target.style.getPropertyValue('--card-rot')) || 0,
      scale: 1,
      opacity: 1,
      duration: 0.48,
      stagger: staggerSec,
      ease: easeString,
      force3D: true
    }, 0.32);

    // 420ms: Kinetic SplitType word typography slide & tilt
    const words = this.overlay.querySelectorAll('.p5r-card-title .word');
    if (words.length > 0) {
      this.timeline.fromTo(words, {
        x: -35,
        rotate: -5,
        opacity: 0
      }, {
        x: 0,
        rotate: 0,
        opacity: 1,
        duration: 0.38,
        stagger: 0.03,
        ease: 'power3.out',
        force3D: true
      }, 0.42);
    }

    // 520ms: Editorial Magazine Dossier panel fades upward with rotational snap
    if (this.descPanel) {
      this.timeline.to(this.descPanel, {
        y: 0,
        rotate: 0,
        opacity: 1,
        duration: 0.38,
        ease: 'back.out(1.8)',
        force3D: true
      }, 0.52);
    }

    // Exit Button Entrance (Top-Right)
    if (this.closeBtn) {
      this.timeline.to(this.closeBtn, {
        opacity: 1,
        y: 0,
        rotate: 0,
        duration: 0.35,
        ease: 'back.out(1.7)'
      }, 0.28);
    }

    // Home Back Button Entrance (Bottom-Right)
    if (this.homeBackBtn) {
      this.timeline.to(this.homeBackBtn, {
        opacity: 1,
        y: 0,
        x: 0,
        duration: 0.35,
        ease: 'back.out(1.7)'
      }, 0.32);
    }
  }

  /* Screen Micro Impact Jitter Effect */
  triggerImpactJitter() {
    if (typeof gsap === 'undefined') return;
    const grid = this.overlay.querySelector('.p5r-nav-grid');
    if (grid) {
      gsap.fromTo(grid, { x: 5, y: -4 }, { x: 0, y: 0, duration: 0.1, ease: 'rough({strength: 2, points: 5, template: linear})' });
    }
  }

  /* Sub-pixel Idle Floating Physics Loop (Breathing Animation) */
  startIdlePhysics() {
    if (typeof gsap === 'undefined' || this.idleTimeline) return;
    if (this.overlay.dataset.idleEnabled === 'false') return;

    this.idleTimeline = gsap.timeline({ repeat: -1, yoyo: true });


    // Menu Card Stack Floating Physics
    if (this.cardStack) {
      this.idleTimeline.to(this.cardStack, {
        y: '+=4',
        rotate: '+=0.4',
        duration: 4.2,
        ease: 'sine.inOut'
      }, 0);
    }

    // Black Polygon Background Continuous Subtle Motion
    if (this.blackPanel) {
      this.idleTimeline.to(this.blackPanel, {
        x: '+=10',
        y: '-=12',
        rotate: '+=0.5',
        duration: 6.5,
        ease: 'sine.inOut'
      }, 0);
    }

    // Magazine Dossier Floating Physics (Opposing Rhythm)
    if (this.descPanel) {
      this.idleTimeline.to(this.descPanel, {
        y: '-=6',
        rotate: '-=0.5',
        duration: 5.0,
        ease: 'sine.inOut'
      }, 0);
    }

    // Ambient Shadow Drift Vignette Idle Physics
    if (this.shadowDrift) {
      this.idleTimeline.to(this.shadowDrift, {
        scale: 1.08,
        rotate: 3,
        duration: 6.5,
        ease: 'sine.inOut'
      }, 0);
    }
  }

  stopIdlePhysics() {
    if (this.idleTimeline) {
      this.idleTimeline.kill();
      this.idleTimeline = null;
    }
    if (this.cardStack) {
      gsap.set(this.cardStack, { y: 0, rotate: 0 });
    }
    if (this.blackPanel) {
      gsap.set(this.blackPanel, { x: 0, y: 0, rotate: 0 });
    }
  }

  play() {
    this.stopIdlePhysics();
    if (this.timeline) {
      this.timeline.restart();
    }
  }

  /* Dedicated Persona 5 Royal Kinetic Exit Animation Timeline */
  exit(onCompleteCallback) {
    this.stopIdlePhysics();
    if (typeof gsap === 'undefined') {
      if (onCompleteCallback) onCompleteCallback();
      return;
    }

    if (this.timeline) {
      this.timeline.pause();
    }

    const exitTl = gsap.timeline({
      onComplete: () => {
        if (onCompleteCallback) onCompleteCallback();
      }
    });

    // 1. Title Header Stamp slides out left with kinetic rotation tilt
    if (this.titleHeader) {
      exitTl.to(this.titleHeader, {
        x: -80,
        rotate: -12,
        opacity: 0,
        duration: 0.22,
        ease: 'power3.in',
        force3D: true
      }, 0);
    }

    // 2. Action Confirm Badge slides down fast
    if (this.confirmPrompt) {
      exitTl.to(this.confirmPrompt, {
        y: 35,
        opacity: 0,
        duration: 0.2,
        ease: 'power2.in',
        force3D: true
      }, 0);
    }

    // 3. Top HUD Stamp slides out left
    if (this.hudStamp) {
      exitTl.to(this.hudStamp, {
        x: -40,
        opacity: 0,
        duration: 0.2,
        ease: 'power2.in',
        force3D: true
      }, 0.02);
    }

    // 4. Background Graphic Stamps fade out
    if (this.graphicStamps.length > 0) {
      exitTl.to(this.graphicStamps, {
        opacity: 0,
        scale: 1.2,
        duration: 0.2,
        ease: 'power2.in'
      }, 0);
    }

    // 5. Asymmetrical Menu Cards fly out left in reverse stagger wave (bottom to top)
    if (this.cardWrappers.length > 0) {
      exitTl.to(this.cardWrappers, {
        x: -240,
        rotate: -22,
        opacity: 0,
        scale: 0.88,
        duration: 0.28,
        stagger: {
          each: 0.025,
          from: 'end'
        },
        ease: 'power4.in',
        force3D: true
      }, 0.03);
    }

    // 6. Magazine Dossier Panel tilts and drops down
    if (this.descPanel) {
      exitTl.to(this.descPanel, {
        y: 65,
        rotate: 8,
        opacity: 0,
        duration: 0.25,
        ease: 'power3.in',
        force3D: true
      }, 0.05);
    }

    // 7. Angular Red Polygon, Top Red Slash & Black Background slide out left
    if (this.topSlash) {
      exitTl.to(this.topSlash, {
        scaleX: 0,
        duration: 0.2,
        ease: 'power3.in'
      }, 0.04);
    }

    if (this.closeBtn) {
      exitTl.to(this.closeBtn, {
        opacity: 0,
        y: -30,
        rotate: 8,
        duration: 0.22,
        ease: 'power3.in',
        force3D: true
      }, 0);
    }

    if (this.homeBackBtn) {
      exitTl.to(this.homeBackBtn, {
        opacity: 0,
        y: 30,
        x: 30,
        duration: 0.22,
        ease: 'power3.in',
        force3D: true
      }, 0.02);
    }

    if (this.redPoly) {
      exitTl.to(this.redPoly, {
        xPercent: -100,
        duration: 0.25,
        ease: 'power3.in'
      }, 0.08);
    }

    if (this.blackPanel) {
      exitTl.to(this.blackPanel, {
        xPercent: -100,
        duration: 0.22,
        ease: 'expo.in'
      }, 0.12);
    }

    // 8. Backdrop Overlay & Ambient Shadow Drift fade out
    if (this.shadowDrift) {
      exitTl.to(this.shadowDrift, {
        opacity: 0,
        scale: 0.75,
        duration: 0.22,
        ease: 'power2.in'
      }, 0.1);
    }

    if (this.backdrop) {
      exitTl.to(this.backdrop, {
        opacity: 0,
        duration: 0.2,
        ease: 'power2.inOut'
      }, 0.15);
    }
  }

  reverse(callback) {
    this.exit(callback);
  }
}

window.Persona5NavigationMotion = Persona5NavigationMotion;



